import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useNavigationHistory } from "../hooks/useNavigationHistory";
import { useSAPDataStore } from "../store/SAPDataStore";
import { useDashboardTabsStore } from "../store/DashboardTabsStore";
import LazyMachineGrid from "../components/LazyMachineGrid";
import LineConfigPanel from "../components/LineConfigPanel";
import LineOverview from "../components/LineOverview";
import ShiftAnalysis from "../components/shiftAnalysis/ShiftAnalysis";
import Dashboard from "../components/Dashboard";
import { styles, cx } from "../styles";
import { useTheme, useThemeClasses } from "../providers/ThemeProvider";
import SortableTabBar from "../components/SortableTabBar";
import { getTabIcon } from "../components/tabIcons";
import LineLiveShift from "../components/live/LineLiveShift";
import { useLineLiveShift } from "../components/live/useLineLiveShift";
import { useLineStations } from "../components/line/useLineData";

const FIXED_LINE_TAB_IDS = ["liveShift", "overview", "shiftAnalysis", "machines"];

/** Machines asked for live data, in line order (a few "lines" are plant buckets). */
const MAX_LIVE_MACHINES = 30;
/** Live data of the open Live Shift tab is read this often… */
const LIVE_POLL_MS = 15_000;
/** …otherwise only whether the line has live data at all. */
const LIVE_PROBE_MS = 5 * 60_000;

// Tab type
interface Tab {
  id: string;
  label: string;
}

// Get initial tabs for line view
const getInitialLineTabs = (): Tab[] => [
  { id: "overview", label: "Overview" },
  { id: "shiftAnalysis", label: "Shift Analysis" },
  { id: "machines", label: "Machines" },
];

const LineView = () => {
  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  const [configPanelOpen, setConfigPanelOpen] = useState(false);
  const { recordVisit } = useNavigationHistory();
  const navigate = useNavigate();
  const { lineId } = useParams<{ lineId: string }>();
  const location = useLocation();

  const {
    hasHydrated,
    initializeMachineTabState,
    getMachineTabState,
    setActiveTab,
    addTab,
    removeTab,
    moveTab,
    setTabs,
  } = useDashboardTabsStore();

  const state =
    (location.state as {
      lineName?: string;
      lineNumber?: string;
      factory?: string;
      machineCount?: string;
      lineType?: string;
      supervisor?: string;
    }) || {};

  // Get line data from store
  const lines = useSAPDataStore((s) => s.lines);
  const lineFromStore = lines.find(
    (l) => l.lineId === lineId || l.linieBK === lineId,
  );

  // Use route params and state, with fallbacks
  const displayLineId = lineId || "line-01";
  const displayLineName =
    state.lineName ||
    lineFromStore?.linieName ||
    `Line ${lineId}` ||
    "Production Line 01";

  // Use line- prefix for tab state key to avoid collision with machine IDs
  const tabStateKey = `line-${displayLineId}`;

  // Record the visit when component mounts
  useEffect(() => {
    recordVisit("line", displayLineId, displayLineName);
  }, [recordVisit, displayLineId, displayLineName]);

  // Initialize line tab state when store is hydrated
  useEffect(() => {
    if (hasHydrated && displayLineId) {
      const initialTabs = getInitialLineTabs();
      initializeMachineTabState(tabStateKey, initialTabs, "overview");

      // Migration: returning users have persisted tab state from before the
      // Shift Analysis tab existed. Inject it (after Overview) if missing so
      // everyone gets the new system tab without losing their custom tabs.
      const existing = getMachineTabState(tabStateKey);
      if (
        existing &&
        !existing.tabs.some((tab) => tab.id === "shiftAnalysis")
      ) {
        const overviewIdx = existing.tabs.findIndex(
          (tab) => tab.id === "overview",
        );
        const insertAt = overviewIdx >= 0 ? overviewIdx + 1 : 0;
        const newTabs = [...existing.tabs];
        newTabs.splice(insertAt, 0, {
          id: "shiftAnalysis",
          label: "Shift Analysis",
        });
        setTabs(tabStateKey, newTabs);
      }
    }
  }, [
    hasHydrated,
    displayLineId,
    tabStateKey,
    initializeMachineTabState,
    getMachineTabState,
    setTabs,
  ]);

  // Get current line tab state
  const lineTabState = hasHydrated ? getMachineTabState(tabStateKey) : null;
  const tabs = lineTabState?.tabs || [];
  const activeTabId = lineTabState?.activeTabId || "overview";

  // Live data of the line's machines (in line order, also those without data
  // in the cockpit yet): read every 15 s while the Live Shift tab is open,
  // otherwise only probed now and then to know whether to show the tab.
  const liveStations = useLineStations(displayLineId, { includeWithoutData: true });
  const liveMachines = useMemo(
    () => liveStations.slice(0, MAX_LIVE_MACHINES),
    [liveStations],
  );
  const liveSns = useMemo(() => liveMachines.map((s) => s.sn), [liveMachines]);
  const liveTabOpen = activeTabId === "liveShift";
  const live = useLineLiveShift(liveSns, {
    full: liveTabOpen,
    intervalMs: liveTabOpen ? LIVE_POLL_MS : LIVE_PROBE_MS,
  });

  // The Live Shift tab leads while a machine of the line sends live data,
  // as on a machine page; it goes once none does (not while still probing).
  useEffect(() => {
    if (!hasHydrated || live.loading) return;
    const current = getMachineTabState(tabStateKey);
    if (!current) return;
    const hasTab = current.tabs.some((tab) => tab.id === "liveShift");
    if (live.hasAny && !hasTab) {
      setTabs(tabStateKey, [
        { id: "liveShift", label: "Live Shift" },
        ...current.tabs,
      ]);
      if (current.activeTabId === "overview" || current.activeTabId === "") {
        setActiveTab(tabStateKey, "liveShift");
      }
    } else if (!live.hasAny && hasTab) {
      setTabs(
        tabStateKey,
        current.tabs.filter((tab) => tab.id !== "liveShift"),
      );
      if (current.activeTabId === "liveShift") {
        setActiveTab(tabStateKey, "overview");
      }
    }
  }, [
    hasHydrated,
    live.loading,
    live.hasAny,
    tabStateKey,
    tabs.length,
    getMachineTabState,
    setTabs,
    setActiveTab,
  ]);

  // Get machines for this line
  const showOnlyWithData = useSAPDataStore((s) => s.showOnlyWithData);
  const allMachinesFull = useSAPDataStore((s) => s.machines);
  const allMachinesWithData = useSAPDataStore((s) => s.machinesWithData);

  // All machines in this line (for config panel)
  const allLineMachines = useMemo(() => {
    return allMachinesFull.filter(
      (machine) => machine.lineId === displayLineId,
    );
  }, [allMachinesFull, displayLineId]);

  // Filtered machines for display (respects toggle)
  const machines = useMemo(() => {
    const sourceList = showOnlyWithData ? allMachinesFull : allMachinesWithData;
    return sourceList.filter((machine) => machine.lineId === displayLineId);
  }, [showOnlyWithData, allMachinesFull, allMachinesWithData, displayLineId]);

  // Handle machine card click
  const handleMachineClick = (machine: any) => {
    navigate(`/machine/${machine.maschinennummer || machine.maschineBK}`, {
      state: {
        machineName:
          machine.machineName ||
          `${machine.bautyp} (${machine.maschinennummer})`,
        lineId: displayLineId,
        lineName: displayLineName,
        factory: state.factory,
        machineType: machine.bautyp || machine.machineType,
      },
    });
  };

  // Tab management handlers
  const handleTabClick = (id: string) => {
    setActiveTab(tabStateKey, id);
  };

  const handleRemoveTab = (id: string) => {
    removeTab(tabStateKey, id);
  };

  // Modal state for adding tabs
  const [showAddModal, setShowAddModal] = useState(false);
  const [pendingDefaultName, setPendingDefaultName] = useState("");

  const getNextDashboardNumber = (
    existingTabs: { label: string }[],
  ): number => {
    let n = 1;
    const usedNumbers = new Set<number>();
    existingTabs.forEach((tab) => {
      const match = tab.label.match(/^My Dashboard (\d+)$/);
      if (match) usedNumbers.add(Number(match[1]));
    });
    while (usedNumbers.has(n)) n++;
    return n;
  };

  const handleAddTab = () => {
    const nextNumber = getNextDashboardNumber(tabs);
    setPendingDefaultName(`My Dashboard ${nextNumber}`);
    setShowAddModal(true);
  };

  const handleAddTabModalSubmit = (name: string) => {
    let finalName = name;
    let suffix = 2;
    while (tabs.some((tab) => tab.label === finalName)) {
      finalName = `${name} (${suffix++})`;
    }
    const newTab = { id: `tab-${Date.now()}`, label: finalName };
    addTab(tabStateKey, newTab);
  };

  // Reorder tabs (called by the shared tab bar's drag-and-drop).
  const handleReorder = (activeId: string, overId: string) => {
    const newIndex = tabs.findIndex((tab) => tab.id === overId);
    if (newIndex !== -1) moveTab(tabStateKey, activeId, newIndex);
  };

  // Modal component for adding tabs
  function AddTabModal({
    isOpen,
    onClose,
    onSubmit,
    defaultName,
    existingNames,
  }: {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (name: string) => void;
    defaultName: string;
    existingNames: string[];
  }) {
    const [name, setName] = useState(defaultName);
    const [error, setError] = useState("");

    useEffect(() => {
      setName(defaultName);
      setError("");
    }, [isOpen, defaultName]);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = name.trim();
      if (!trimmed) {
        setError(t("nameRequired") || "Name cannot be empty.");
        return;
      }
      if (existingNames.includes(trimmed)) {
        setError(t("nameMustBeUnique") || "Name must be unique.");
        return;
      }
      onSubmit(trimmed);
      onClose();
    };

    if (!isOpen) return null;
    return (
      <div className={styles.modal.overlay}>
        <div
          className={styles.modal.container}
          style={{
            backgroundColor: "var(--bg-secondary)",
            borderColor: "var(--border-primary)",
          }}
        >
          <h2
            className={styles.modal.header.title}
            style={{ color: "var(--text-primary)" }}
          >
            {t("addNewDashboardTab") || "Add New Dashboard Tab"}
          </h2>
          <form onSubmit={handleSubmit}>
            <label
              className={styles.modal.form.label}
              style={{ color: "var(--text-primary)" }}
            >
              {t("tabName") || "Tab Name"}
            </label>
            <input
              className={styles.modal.form.input}
              style={{
                backgroundColor: "var(--bg-primary)",
                borderColor: "var(--border-primary)",
                color: "var(--text-primary)",
              }}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              maxLength={40}
            />
            {error && <div className={styles.modal.form.error}>{error}</div>}
            <div className={styles.modal.actions.container}>
              <button
                type="button"
                onClick={onClose}
                className={styles.modal.actions.cancel}
                style={{
                  backgroundColor: "var(--bg-primary)",
                  color: "var(--text-secondary)",
                }}
              >
                {t("cancel") || "Cancel"}
              </button>
              <button
                type="submit"
                className={styles.modal.actions.submit}
                style={{ backgroundColor: "#2563eb", color: "white" }}
              >
                {t("add") || "Add"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Show loading state while store is hydrating
  if (!hasHydrated) {
    return (
      <div className={styles.home.container}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.home.container}>
      {/* Shared tab bar (pill on desktop, bottom nav on mobile) */}
      <SortableTabBar
        tabs={tabs}
        activeTabId={activeTabId}
        fixedTabIds={FIXED_LINE_TAB_IDS}
        onTabClick={handleTabClick}
        onReorder={handleReorder}
        onAddTab={handleAddTab}
        onRemoveTab={handleRemoveTab}
        getIcon={getTabIcon}
        labelFor={(tab) => t(tab.id) || tab.label}
        rightSlot={
          <button
            onClick={() => setConfigPanelOpen(true)}
            className={cx(
              "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
              "bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md",
            )}
            title={t("configureLineKPI") || "Configure Line KPI"}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span className="hidden sm:inline">
              {t("kpiConfig") || "KPI Config"}
            </span>
          </button>
        }
      />

      {/* Content area */}
      <div
        className={cx(styles.sortableTabs.content.container, "pb-24 sm:pb-4")}
        style={{
          backgroundColor: currentTheme.isDark
            ? "var(--bg-secondary)"
            : "#f3f4f6",
          transition: "background-color 0.3s ease",
        }}
      >
        {activeTabId === "liveShift" ? (
          <LineLiveShift
            lineId={displayLineId}
            stations={liveMachines}
            live={live.data}
            loading={live.loading}
          />
        ) : activeTabId === "overview" ? (
          <LineOverview lineId={displayLineId} machines={machines} />
        ) : activeTabId === "shiftAnalysis" ? (
          <ShiftAnalysis
            lineId={displayLineId}
            lineName={displayLineName}
            factory={state.factory}
            machines={allLineMachines}
          />
        ) : activeTabId === "machines" ? (
          <LineMachinesTab
            machines={allLineMachines}
            onMachineClick={handleMachineClick}
          />
        ) : (
          <Dashboard
            key={activeTabId}
            machineId={displayLineId}
            machineName={displayLineName}
            dashboardId={activeTabId}
            dashboardName={
              tabs.find((tab) => tab.id === activeTabId)?.label || "Dashboard"
            }
          />
        )}
      </div>

      {/* Line KPI Configuration Panel */}
      <LineConfigPanel
        lineId={displayLineId}
        lineName={displayLineName}
        machines={allLineMachines}
        isOpen={configPanelOpen}
        onClose={() => setConfigPanelOpen(false)}
      />

      <AddTabModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddTabModalSubmit}
        defaultName={pendingDefaultName}
        existingNames={tabs.map((tab) => tab.label)}
      />
    </div>
  );
};

// ─── Machines Tab Component ─────────────────────────────────────────────────
interface LineMachinesTabProps {
  machines: any[];
  onMachineClick: (machine: any) => void;
}

const LineMachinesTab: React.FC<LineMachinesTabProps> = ({
  machines,
  onMachineClick,
}) => {
  const { t } = useTranslation();
  const themeClasses = useThemeClasses();

  return (
    <div className="p-6">
      <div className="mb-4">
        <h2 className={cx("text-xl font-semibold", themeClasses.textPrimary)}>
          {t("machinesList") || "Machines"}
        </h2>
        <p className={cx("text-sm", themeClasses.textSecondary)}>
          {t("machinesListAllDescription") || "All machines in this line"}
        </p>
      </div>

      {machines.length === 0 ? (
        <div className={cx("text-center py-12", themeClasses.textMuted)}>
          <div className="text-6xl mb-4">⚙️</div>
          <p className={cx("text-sm", themeClasses.textMuted)}>
            {t("noMachinesInLine") || "No machines available in this line."}
          </p>
        </div>
      ) : (
        <LazyMachineGrid
          machines={machines}
          onMachineClick={onMachineClick}
          initialLoadCount={12}
          loadMoreCount={12}
        />
      )}
    </div>
  );
};

export default LineView;
